import type { RecordDto } from '@halo/shared';
import type { WeekColumn } from '../dates';
import { applyChanges, compactData, readString, type RecordData } from './parsing';

/**
 * Nhóm dòng trong bảng Thị trường. `own` (thêm 23/09/2026) là KÊNH CỦA CHÍNH CÔNG TY đưa vào bảng để
 * so cùng thước đo với đối thủ: cùng một con số «view cả kênh trong tuần», gõ tay như nhau, nên cộng
 * lại mới ra tổng thị trường và tính được phần của mình trong đó.
 */
/**
 * Nhóm của một dòng trong bảng Thị trường.
 *
 * «market» — TÌM KIẾM THỊ TRƯỜNG (thêm 26/09/2026): kênh đang dò xem có đáng làm không, chưa phải
 * đối thủ của mảng nào. Để lẫn vào «đối thủ trực tiếp» thì bảng đối thủ phình ra toàn kênh chỉ mới
 * ngó qua.
 */
export type CompetitorGroup = 'own' | 'direct' | 'indirect' | 'market';

export interface Competitor {
  recordId: number;
  owner: string | null;
  group: CompetitorGroup;
  /** Có thể rỗng (giao diện hiện «bấm để đặt tên»). */
  name: string;
  topic: string;
  url: string;
  /**
   * SỐ LIỆU THẬT: lượt xem cả kênh theo tuần, khoá = «DD/MM/YYYY» của ngày Chủ nhật. Giá trị CÓ THỂ
   * ÂM (đối thủ xoá/ẩn video); khoá VẮNG MẶT = chưa thu thập (khác 0).
   */
  weeklyViews: Readonly<Record<string, number>>;
  /** Ghi chú theo dõi kênh («xoá video tuần 3», «đổi tên»…) — thêm 22/09/2026, khoá `note`. */
  note: string;
  /**
   * Vị trí do người dùng tự xếp trong bảng (khoá `ord`, thêm 22/09/2026); `null` = chưa xếp → đứng
   * sau các dòng đã xếp, theo thứ tự nhập (xem `sortCompetitors` ở `domain/market.ts`).
   */
  order: number | null;
  /**
   * Dòng nhóm «Kênh của mình» nối tới KÊNH nào (mã kênh, khoá `ch`, thêm 23/09/2026); '' = không
   * nối. Có mã thì số theo tuần lấy thẳng được từ mục «Nhập chỉ số» thay vì gõ tay.
   */
  channelCode: string;
  /**
   * Mã YouTube của KÊNH BÊN NGHIÊN CỨU ĐỐI THỦ mà dòng này ứng với (khoá `yt`, thêm 26/09/2026);
   * '' = chưa nối. Nối rồi thì lần sau lấy view tuần khớp thẳng, không phải đoán lại từ link hay
   * tên — link người ta sửa, tên đối thủ đổi, còn mã kênh thì không.
   */
  researchId: string;
}

function readWeeklyViews(value: unknown): Record<string, number> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  return Object.fromEntries(
    Object.entries(value).filter(
      (entry): entry is [string, number] =>
        typeof entry[1] === 'number' && Number.isFinite(entry[1]),
    ),
  );
}

const GROUPS: readonly CompetitorGroup[] = ['own', 'direct', 'indirect', 'market'];

/** Giá trị lạ (dữ liệu cũ, bản web cũ) → «đối thủ trực tiếp», như nếp có từ hệ thống cũ. */
function readGroup(value: unknown): CompetitorGroup {
  return typeof value === 'string' && (GROUPS as readonly string[]).includes(value)
    ? (value as CompetitorGroup)
    : 'direct';
}

export function parseCompetitor(record: RecordDto): Competitor {
  const data = record.data;
  return {
    recordId: record.id,
    owner: record.owner,
    // Mọi giá trị LẠ đều là đối thủ trực tiếp — đúng cách hệ thống cũ chia nhóm.
    // Mọi giá trị LẠ vẫn là đối thủ trực tiếp — đúng cách hệ thống cũ chia nhóm.
    group: readGroup(data.grp),
    name: readString(data, 'name'),
    topic: readString(data, 'topic'),
    url: readString(data, 'url'),
    weeklyViews: readWeeklyViews(data.wk),
    note: readString(data, 'note'),
    order: typeof data.ord === 'number' && Number.isFinite(data.ord) ? data.ord : null,
    channelCode: readString(data, 'ch'),
    researchId: readString(data, 'yt'),
  };
}

function toCompetitorData(competitor: Competitor): RecordData {
  return {
    grp: competitor.group,
    name: competitor.name,
    topic: competitor.topic,
    url: competitor.url,
    wk: competitor.weeklyViews,
    // Rỗng / chưa xếp thì KHÔNG ghi khoá — bản ghi của hệ thống cũ không có các khoá này.
    note: competitor.note || undefined,
    ord: competitor.order ?? undefined,
    ch: competitor.channelCode || undefined,
    yt: competitor.researchId || undefined,
  };
}

/**
 * Có `stored` (đang SỬA): chỉ ghi đè khoá thật sự đổi. Mảng hiển thị `w` của bản cũ KHÔNG bao giờ
 * được ghi: nó phụ thuộc số cột tuần đang mở trên từng máy, ghi nó là mỗi lần đổi số cột lại viết
 * lại hàng chục bản ghi.
 */
export function serializeCompetitor(competitor: Competitor, stored?: RecordDto): RecordData {
  const next = toCompetitorData(competitor);
  if (!stored) return compactData(next);
  return compactData(applyChanges(stored.data, toCompetitorData(parseCompetitor(stored)), next));
}

/** Giá trị theo từng cột tuần đang hiển thị; `null` = chưa thu thập. */
export function viewsByWeek(
  competitor: Competitor,
  weeks: readonly WeekColumn[],
): (number | null)[] {
  return weeks.map((week) => competitor.weeklyViews[week.key] ?? null);
}

/** Đặt / xoá số của một tuần. Ô để trống là XOÁ khoá, không phải ghi 0. */
export function withWeeklyViews(
  competitor: Competitor,
  weekKey: string,
  value: number | null,
): Competitor {
  const weeklyViews = { ...competitor.weeklyViews };
  if (value === null) delete weeklyViews[weekKey];
  else weeklyViews[weekKey] = value;
  return { ...competitor, weeklyViews };
}
